import { SafeHydrate } from "@/SafeHydrate";
import { Quote } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
type Testimonial = {
  quote: string;
  highlight: string;
  name: string;
  role: string;
  company: string;
  avatar: string;
  featured: boolean;
};
const testimonials: Testimonial[] = [{
  quote: "AthenaCare completely changed how we manage my father's daily medication. We eliminated the stress of pill sorting and finally have peace of mind knowing he never misses a dose.",
  highlight: "eliminated the stress of pill sorting",
  name: "Sarah Jenkins",
  role: "Family Caregiver",
  company: "Home Support",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_sarah_jenkins.webp",
  featured: true
}, {
  quote: "The MedaCube dispenser is incredibly intuitive. It took the guesswork out of our routine, and the automated alerts are a game changer for our busy schedule.",
  highlight: "took the guesswork out of our routine",
  name: "David Miller",
  role: "Primary Caregiver",
  company: "Senior Living",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_david_miller.webp",
  featured: false
}, {
  quote: "Our weekly medication prep used to take hours. Now, the system handles everything, and I can monitor adherence from my phone while at work.",
  highlight: "monitor adherence from my phone",
  name: "Elena Rodriguez",
  role: "Daughter & Caregiver",
  company: "Family Care",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_elena_rodriguez.png",
  featured: false
}, {
  quote: "We saved so much time and worry. The automated reminders ensure my husband stays on track with his health plan every single day.",
  highlight: "ensure my husband stays on track",
  name: "Linda Thompson",
  role: "Spouse & Caregiver",
  company: "Senior Wellness",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_linda_thompson.png",
  featured: false
}, {
  quote: "Managing multiple prescriptions was a nightmare until we found AthenaCare. Now, it is one simple, automated process.",
  highlight: "one simple, automated process",
  name: "Robert Chen",
  role: "Caregiver",
  company: "Community Care",
  avatar: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703060/img/caregiver_robert_chen.png",
  featured: false
}];
function TestimonialTile({
  testimonial,
  className,
  size
}: {
  testimonial: Testimonial;
  className?: string;
  size: "featured" | "standard";
}) {
  const parts = testimonial.quote.split(testimonial.highlight);
  return <div className={"group relative flex flex-col justify-between rounded-lg border border-border bg-card transition-all duration-300 ease-out hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg " + (size === "featured" ? "p-8 md:p-10 " : "p-7 md:p-8 ") + (className ?? "")}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <SafeHydrate><Avatar className={"ring-1 ring-border transition-all duration-200 group-hover:ring-primary " + (size === "featured" ? "size-14" : "size-12")}>
            <AvatarImage src={testimonial.avatar} alt={testimonial.name} />
            <AvatarFallback className="bg-secondary text-secondary-foreground text-sm font-default font-semibold">
              {testimonial.name.split(" ").map(n => n[0]).join("")}
            </AvatarFallback>
          </Avatar></SafeHydrate>
          <div className="flex flex-col">
            <span className="font-default font-semibold text-foreground text-sm md:text-base tracking-tight">
              {testimonial.name}
            </span>
            <span className="text-xs md:text-[0.8125rem] text-muted-foreground leading-snug">
              {testimonial.role}
            </span>
            <span className="text-xs md:text-[0.8125rem] text-muted-foreground leading-snug">
              {testimonial.company}
            </span>
          </div>
        </div>
        <Quote className={"text-primary/40 transition-colors duration-200 group-hover:text-primary " + (size === "featured" ? "size-7" : "size-5")} strokeWidth={1.5} />
      </div>

      <p className={"mt-6 text-card-foreground leading-relaxed " + (size === "featured" ? "text-lg md:text-xl tracking-tight" : "text-base md:text-[1.0625rem] tracking-tight")}>
        {parts[0]}
        {parts.length > 1 && <>
            <span className="relative inline whitespace-normal">
              <span className="relative z-10">{testimonial.highlight}</span>
              <span aria-hidden="true" className="absolute left-0 right-0 bottom-0.5 h-[0.4em] bg-primary/25 -z-0" />
            </span>
            {parts.slice(1).join(testimonial.highlight)}
          </>}
      </p>
    </div>;
}
export default function Testimonials() {
  return <section data-section-id="12264" id="verifiedreviews" className="bg-background text-foreground py-24 md:py-32 px-5 md:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mx-auto max-w-[720px] text-center">
          <span className="inline-block text-xs font-medium uppercase tracking-[0.12em] text-primary mb-5">
            Caregiver Stories
          </span>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[3.5rem] font-semibold leading-[1.05] tracking-tight text-foreground">
            Families who chose{" "}
            <span className="italic font-medium text-primary">peace of mind</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed">Caregivers use AthenaCare to simplify medication management, ensuring their loved ones stay healthy and independent at home.</p>
        </div>

        <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4">
          <div data-index={0} className="md:col-span-7">
            <TestimonialTile testimonial={testimonials[0]} size="featured" className="h-full" />
          </div>
          <div data-index={1} className="md:col-span-5">
            <TestimonialTile testimonial={testimonials[1]} size="standard" className="h-full" />
          </div>

          <div data-index={2} className="md:col-span-4">
            <TestimonialTile testimonial={testimonials[2]} size="standard" className="h-full" />
          </div>
          <div data-index={3} className="md:col-span-4">
            <TestimonialTile testimonial={testimonials[3]} size="standard" className="h-full" />
          </div>
          <div data-index={4} className="md:col-span-4">
            <TestimonialTile testimonial={testimonials[4]} size="standard" className="h-full" />
          </div>
        </div>
      </div>
    </section>;
}